import React, { useEffect, useRef } from 'react'
import Button from '@renderer/components/bahela/Button'
import { formatRewardExpiry, type KarcisRewardVoucher } from '@renderer/utils/karcisReward'

import bgLayer from '/assets/bahela/bg-layer.webp'

interface RewardVoucherModalProps {
  isVisible: boolean
  voucher: KarcisRewardVoucher
  onClose: () => void
  /** Seconds before the popup closes itself. */
  timeout?: number
}

/**
 * "Kamu dapat gratis foto karcis!" — the last thing a Bilik Kotak guest sees.
 *
 * The code is the entire point of the screen, so it is the biggest thing on it
 * and everything else is caption. A guest reads it once, walks ten metres and
 * types it into an iPad; that is the whole job, and a layout that made them
 * hunt for it would send them to the team, which is the errand this feature
 * exists to remove (Dhafin, 2026-09-22).
 *
 * It closes itself. The result screen behind it holds a timeout the operator
 * configured (30 seconds on the Kotak today) and the next guest is waiting, so
 * a modal that waited for a tap would strand the booth on the previous
 * customer's voucher — and vouchers are minted per session, so the next guest
 * must never be looking at this one's code.
 */
const RewardVoucherModal: React.FC<RewardVoucherModalProps> = ({
  isVisible,
  voucher,
  onClose,
  timeout = 45
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect((): (() => void) => {
    if (isVisible && timeout > 0) {
      timeoutRef.current = setTimeout(() => onClose(), timeout * 1000)
    }
    return (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [isVisible, timeout, onClose])

  if (!isVisible) return null

  const expiry = formatRewardExpiry(voucher.expiresAt)

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-8">
      <div
        className="absolute inset-0 bg-black/90 bg-cover"
        style={{ backgroundImage: `url(${bgLayer})` }}
      />

      <div className="relative bg-methadone/95 backdrop-blur-md rounded-3xl shadow-2xl border-4 border-uriYellow px-12 py-10 max-w-4xl w-full flex flex-col items-center text-center">
        {/* No count in the headline. The voucher covers the whole karcis
            session, and how many karcis that prints is the project's
            captureCount — three at Cikstik today, and changeable from the
            Studio without anyone thinking to come back here. "Satu" would be
            wrong the moment it moves. */}
        <h1 className="text-[52px] leading-tight font-serif-bahela text-uriYellow drop-shadow-2xl">
          Gratis foto karcis!
        </h1>

        <p className="mt-3 text-[26px] text-white font-body-bahela drop-shadow-lg">
          Tunjukkan kode ini di booth Foto Karcis sebelah
        </p>

        {/* The code. Monospace and wide-tracked because it is going to be
            copied character by character onto a touchscreen, and the alphabet
            the server draws from already excludes every glyph pair a guest can
            confuse (config/karcisReward.ts). */}
        <div className="mt-7 w-full rounded-2xl border-4 border-dashed border-uriYellow bg-black/30 px-8 py-6">
          <span className="block font-mono font-bold text-uriYellow text-[76px] leading-none tracking-[0.12em] drop-shadow-2xl break-all">
            {voucher.code}
          </span>
        </div>

        <p className="mt-5 text-[24px] text-white font-body-bahela drop-shadow-lg">
          {expiry
            ? `Berlaku untuk 1x pemakaian, sampai ${expiry}`
            : 'Berlaku untuk 1x pemakaian'}
        </p>

        <Button className="mt-8 text-[28px]" onClick={onClose}>
          Sudah dicatat
        </Button>
      </div>
    </div>
  )
}

export default RewardVoucherModal
